import { useEffect, useRef, useState } from 'react'
import { create } from 'zustand'
import {
  ArrowDown,
  ArrowRight,
  BarChart3,
  BoxSelect,
  Briefcase,
  Building2,
  Calculator,
  CalendarClock,
  Check,
  Coins,
  Columns2,
  Download,
  Egg,
  FileCode2,
  FileSpreadsheet,
  FileUp,
  Info,
  Landmark,
  LassoSelect,
  LayoutTemplate,
  ListChecks,
  MousePointer2,
  RefreshCw,
  Scale,
  ShieldCheck,
  SlidersHorizontal,
  Sparkles,
  UsersRound,
} from 'lucide-react'
import { Link } from 'react-router-dom'
import { getStudioHref } from '@/lib/hostRouting'
import { EXAMPLE_EMPLOYEE_COUNT } from '@/lib/demoLimits'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import Seo from '@/components/Seo'
import PublicBirds from '@/components/public/PublicBirds'
import RotatingWord from '@/components/public/RotatingWord'
import { PublicSection, SectionHead } from '@/components/public/Section'
import { AuthHide, PublicPrimaryCta, usePublicAuthed } from '@/components/public/PublicCtas'
import { FlightPathMark, JulioAvatar } from '@/components/public/motifs'
import { TEAM, TEAM_NOTE } from '@/content/team'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { HOME_FAQS } from '@/lib/seo'
import { prefetchBookPage } from '@/lib/prefetchBook'
import { CHART_COLORS, SELECTION_COLORS } from '@/lib/constants'

// Pulled straight from the app's palette (src/lib/constants.js) so the previews
// read as the real calculator rather than stock illustration.
const C = {
  primary: CHART_COLORS.primary,
  violet: CHART_COLORS.secondary,
  success: CHART_COLORS.success,
  hce: CHART_COLORS.hce,
  nhce: CHART_COLORS.nhce,
}
const SELECT_BLUE = SELECTION_COLORS.solid

// The hero is a live miniature of the calculator: three interacting levers
// (match formula, vesting schedule, auto-enroll) drive employer cost,
// participation, estimated forfeitures, a morphing vesting curve, and the
// compliance screens – the same shape-the-plan-read-the-answer loop
// the real engine runs. Figures are illustrative of the bundled sample census,
// not live-recomputed here: a curated stand-in written to show the trade-offs
// of each design rather than a guaranteed win.
const MATCH_OPTIONS = [
  { id: 'match50', label: '50% to 6%', note: 'Classic match', cost: 1.74, part: 71 },
  { id: 'match100', label: '100% to 4%', note: 'Dollar-for-dollar', cost: 1.62, part: 74 },
  { id: 'stretch', label: '25% to 12%', note: 'Stretch match', cost: 2.08, part: 68 },
]

// schedule = % vested by year of service (1–6); forf = how much unvested money
// terminations leave behind for the plan to recapture, scaled off match cost.
const VESTING_OPTIONS = [
  { id: 'immediate', label: 'Immediate', note: '100% day one', schedule: [100, 100, 100, 100, 100, 100], forf: 0 },
  { id: 'cliff3', label: '3-yr cliff', note: 'All-or-nothing at 3', schedule: [0, 0, 100, 100, 100, 100], forf: 0.013 },
  { id: 'graded6', label: '6-yr graded', note: '20% a year', schedule: [0, 20, 40, 60, 80, 100], forf: 0.023 },
]

// Auto-enrolling new hires pulls more NHCEs in: higher participation and cost,
// and a tighter ADP-style spread – the kind of trade-off the app surfaces live.
function modelPlan(match, vesting, autoEnroll) {
  const cost = match.cost + (autoEnroll ? 0.23 : 0)
  return {
    cost, // $M
    part: Math.min(match.part + (autoEnroll ? 18 : 0), 93), // %
    forfeitures: cost * vesting.forf * 1000, // $K recaptured / yr
    adp: autoEnroll ? 'pass' : 'watch',
    topHeavy: autoEnroll ? 'clear' : 'watch',
  }
}

const fmtM = (n) => '$' + n.toFixed(2) + 'M'
const fmtK = (n) => (n < 0.05 ? '$0' : '$' + n.toFixed(1) + 'K')

// The Compare tab reads the configured plan against a plain-vanilla baseline
// (classic match, immediate vesting, no auto-enroll) so the deltas tell a story.
const BASELINE = modelPlan(MATCH_OPTIONS[0], VESTING_OPTIONS[0], false)

// The hero's three levers live in a page-level store (not component state) so
// the Outputs results strip further down mirrors the visitor's design live –
// the same numbers thread through the whole page.
const useHeroPlan = create((set) => ({
  matchIdx: 2,
  vestIdx: 2,
  autoEnroll: true,
  setMatchIdx: (matchIdx) => set({ matchIdx }),
  setVestIdx: (vestIdx) => set({ vestIdx }),
  toggleAutoEnroll: () => set((s) => ({ autoEnroll: !s.autoEnroll })),
}))

// Tween a value toward its target with easeOutCubic so the hero stats roll
// rather than snap when a lever changes. Honors prefers-reduced-motion.
function useAnimatedNumber(target, duration = 650) {
  const [display, setDisplay] = useState(target)
  const displayRef = useRef(target)
  const rafRef = useRef(0)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      displayRef.current = target
      setDisplay(target)
      return
    }
    const from = displayRef.current
    const start = performance.now()
    const step = (now) => {
      const t = Math.min(1, (now - start) / duration)
      const v = from + (target - from) * (1 - Math.pow(1 - t, 3))
      displayRef.current = v
      setDisplay(v)
      if (t < 1) rafRef.current = requestAnimationFrame(step)
    }
    cancelAnimationFrame(rafRef.current)
    rafRef.current = requestAnimationFrame(step)
    return () => cancelAnimationFrame(rafRef.current)
  }, [target, duration])

  return display
}

const ROTATING_WORDS = ['modeling', 'comparison', 'analysis', 'testing', 'optimization']

const PROOF_ITEMS = [
  {
    icon: ShieldCheck,
    title: 'Census never uploaded',
    body: 'Every calculation runs in your browser. Exports are generated on your device.',
  },
  {
    icon: Scale,
    title: 'Modeled on the regulations',
    body: 'Screens follow IRC §401(a)(4), §410(b), §416, and §404 methodology, with cited constants.',
  },
  {
    icon: ListChecks,
    title: '300+ automated tests',
    body: 'The calculation engine ships behind a deterministic, regression-tested suite.',
  },
  {
    icon: Info,
    title: 'Planning screens, not filings',
    body: 'Directional models – confirm results with your TPA, actuary, or recordkeeper.',
  },
]

const SPEC = [
  { icon: Columns2, term: 'Compare plans', desc: 'Run up to three alternative designs against your baseline and read the difference on every metric, down to the dollar.' },
  { icon: LayoutTemplate, term: 'Plan presets', desc: 'Start from Safe Harbor, QACA, match-only, or max-generosity, then tweak from there.' },
  { icon: Coins, term: 'Contributions', desc: 'Tiered match, safe harbor, NEC, profit sharing and new comparability, after-tax, Roth, and catch-up – all modeled at once.' },
  { icon: Landmark, term: '401(k) + cash balance', desc: 'Enter explicit year-one hypothetical benefit-credit formulas by group, review combined-plan directional screens, and keep those credits separate from DC cost and actuarial funding.' },
  { icon: CalendarClock, term: 'Eligibility, vesting & terminations', desc: 'Set the rules and watch vested balances and estimated forfeitures move. SECURE 2.0 long-term part-time employees are handled automatically.' },
  { icon: Scale, term: 'Compliance screens', desc: 'Track ADP, ACP, coverage, cross-testing, and top-heavy signals while you model – each with a plain-English why and how to address it.' },
  { icon: BarChart3, term: 'Distributions & demographics', desc: 'Deferral, compensation, age, tenure, and gender views you can click straight into.' },
  { icon: Download, term: 'Export', desc: 'Pull a CSV of any selection, a full PDF summary of the plan, a one-page brief, or your plan settings as reusable JSON.' },
  { icon: Egg, term: 'Estimate Mode', desc: 'Model a full plan from the rough numbers a prospect can recite – a realistic synthetic census, regenerated deterministically from editable assumptions.' },
  { icon: FileSpreadsheet, term: 'Census import', desc: 'Drop the messy spreadsheet a client actually sends. You see it as it will import – columns matched by their values, names combined, cleanups applied – and confirm the few real questions.' },
  { icon: Sparkles, term: 'Julio, the assistant', desc: 'Plain-English questions, proactive insights, staged plan changes, and a goal-seek optimizer – behind a privacy filter that keeps identifiers in your browser.' },
]

const JULIO_PIPELINE = [
  {
    n: '01',
    title: 'Local analysis.',
    body: 'Deterministic tools read your census and plan in the browser.',
  },
  {
    n: '02',
    title: 'Privacy filter.',
    body: 'Only sanitized aggregates and pseudonymous tokens ever leave the page – never a name, ID, or employee row.',
  },
  {
    n: '03',
    title: 'Staged, not applied.',
    body: 'Every proposal lands on a confirmation card with computed before and after values. You apply it, or you don’t.',
  },
]

const STAGED_DESIGNS = [
  { slot: 'Plan B', name: 'Owner-max new comparability', owner: '$72,000', total: '$118,400' },
  { slot: 'Plan C', name: 'Owner-max + 3% safe-harbor NEC', owner: '$72,000', total: '$131,900' },
  { slot: 'Plan D', name: 'Owner-heavy, 2:1 staff ratio', owner: '$34,500', total: '$96,200' },
]

const PERSONAS = [
  {
    icon: Briefcase,
    who: 'Advisors',
    what: 'Walk into the pitch with a design that beats the incumbent – and know how it screens before you present it.',
  },
  {
    icon: Calculator,
    who: 'TPAs & consultants',
    what: 'Screen ADP/ACP, coverage, cross-testing, and top-heavy on a real census in seconds, with the why behind each flag.',
  },
  {
    icon: Landmark,
    who: 'CPAs & finance teams',
    what: 'Read net employer cost and the owner’s number at a glance, then hand over a clean, branded brief.',
  },
  {
    icon: Building2,
    who: 'Plan sponsors',
    what: 'Understand what a plan change costs and who it helps, in plain language – before signing anything.',
  },
]

const WORKFLOW = [
  {
    icon: FileUp,
    step: '01',
    title: 'Load the census',
    description: 'Upload a CSV or enter employees by hand, or start from the built-in sample. Everything is computed in your browser.',
  },
  {
    icon: SlidersHorizontal,
    step: '02',
    title: 'Shape the plan',
    description: 'Set 401(k) contributions, cash-balance benefit-credit terms, eligibility, vesting, and termination rules. Every change recalculates instantly.',
  },
  {
    icon: BarChart3,
    step: '03',
    title: 'Compare the answer',
    description: 'Read costs and compliance signals, inspect employees, compare plan designs side by side, and export the result.',
  },
]

export default function LandingPage() {
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <Seo path="/" />
      <SiteHeader />

      <main className="flex-1">
        <Hero />
        <ProofBand />
        <HowItWorks />
        <WhatItModels />
        <Outputs />
        <JulioSection />
        <AboutSection />
        <Faq />
      </main>

      <SiteFooter />
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Page sections                                                              */
/* -------------------------------------------------------------------------- */

function Hero() {
  // Host-aware: resolves to studio.retira.app/login from the public host.
  const loginHref = getStudioHref('/login')

  return (
    <section className="relative">
      {/* Hidden on phones: the lead bird's pass crosses the headline there. */}
      <PublicBirds className="hidden sm:block" />
      <div className="relative mx-auto grid max-w-6xl gap-12 px-5 py-14 lg:grid-cols-[0.92fr_1.08fr] lg:items-start lg:py-20">
        <div className="lg:pt-4 xl:pt-6">
          <h1 className="text-4xl font-semibold leading-[1.16] tracking-tight sm:text-5xl">
            Instant 401(k)
            <br />
            plan <RotatingWord words={ROTATING_WORDS} />
          </h1>
          <p className="mt-6 max-w-xl text-base leading-7 text-muted-foreground sm:text-lg">
            Compare plans to see how every decision impacts cost, participation, and compliance. Built for
            advisors, consultants, and employers seeking a win-win: lower employer spending and a healthier
            nest egg for every employee.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <PublicPrimaryCta size="lg" withArrow />
            <Button asChild size="lg" variant="outline">
              <Link to="/book" onPointerEnter={prefetchBookPage} onFocus={prefetchBookPage}>
                Book a call
              </Link>
            </Button>
            <AuthHide>
              <Button asChild size="lg" variant="ghost">
                <a href={loginHref}>Log in</a>
              </Button>
            </AuthHide>
          </div>
          <p className="mt-8 max-w-xl text-sm leading-6 text-muted-foreground">
            Try the demo to explore {EXAMPLE_EMPLOYEE_COUNT} sample employees or load your own census, no account needed. Or book 30 minutes with the founders to walk through your plan.
          </p>
        </div>

        <figure className="m-0">
          <ComparePreview />
          <figcaption className="mx-auto mt-3 max-w-xl text-center text-xs leading-5 text-muted-foreground">
            A live slice of the app – switch tabs and tweak the plan to watch it recompute.
          </figcaption>
        </figure>
      </div>
    </section>
  )
}

/** Methodology band: the four trust facts, side by side under the hero. */
function ProofBand() {
  return (
    <section className="border-t border-border bg-card">
      <div className="mx-auto grid max-w-6xl gap-x-8 gap-y-6 px-5 py-7 sm:grid-cols-2 lg:grid-cols-4">
        {PROOF_ITEMS.map(({ icon: Icon, title, body }) => (
          <div key={title} className="flex gap-3">
            <Icon className="mt-0.5 h-5 w-5 shrink-0 text-foreground" aria-hidden="true" />
            <div>
              <h3 className="text-sm font-semibold">{title}</h3>
              <p className="mt-1 text-[13px] leading-6 text-muted-foreground">{body}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}

function HowItWorks() {
  return (
    <PublicSection id="how-it-works" alt>
      <SectionHead
        eyebrow="How it works"
        title="From census to answers in minutes."
        body="Three steps: load your census, set the plan rules, and read the answer. No waiting on a spreadsheet."
      />

      <ol className="mt-9 grid border-y border-border md:grid-cols-3 md:divide-x md:divide-border">
        {WORKFLOW.map(({ icon: Icon, step, title, description }) => (
          <li key={step} className="border-b border-border py-6 last:border-b-0 md:border-b-0 md:px-6 md:first:pl-0 md:last:pr-0">
            <div className="flex items-center justify-between">
              <Icon className="h-5 w-5 text-primary" aria-hidden="true" />
              <span className="font-mono text-xs text-muted-foreground">{step}</span>
            </div>
            <h3 className="mt-5 text-base font-semibold">{title}</h3>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">{description}</p>
          </li>
        ))}
      </ol>
    </PublicSection>
  )
}

function Outputs() {
  // Live-linked to the hero's levers: change the match, vesting, or
  // auto-enroll up top and this strip recomputes down here.
  const { matchIdx, vestIdx, autoEnroll } = useHeroPlan()
  const out = modelPlan(MATCH_OPTIONS[matchIdx], VESTING_OPTIONS[vestIdx], autoEnroll)
  const cost = useAnimatedNumber(out.cost)
  const part = useAnimatedNumber(out.part)
  // Illustrative mapping: higher participation pulls the average deferral up
  // (6.4% at the default design's 86% participation).
  const avgDef = (part * 6.4) / 86

  return (
    <PublicSection id="outputs" alt>
      <SectionHead
        eyebrow="Outputs"
        title="Every result, and every chart is something you can dig into."
        body="See cost, participation, contributions, vesting, compliance, and the employee detail behind every result. Click or drag in a chart to inspect its selection."
      />

      {/* Plan-level results strip, recomputed from the hero's configured design. */}
      <div className="mt-9 border border-border bg-background">
        <p className="border-b border-border/60 bg-muted/20 px-4 py-1 text-[9px] font-semibold uppercase tracking-[0.12em] text-muted-foreground/70">
          Sample plan · {EXAMPLE_EMPLOYEE_COUNT} employees · follows the design you set in the hero
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 sm:divide-x sm:divide-border">
          {[
            ['Employer cost / yr', fmtM(cost)],
            ['Participation', `${Math.round(part)}%`],
            ['Avg deferral', `${avgDef.toFixed(1)}%`],
            ['Eligible', `92 / ${EXAMPLE_EMPLOYEE_COUNT}`],
          ].map(([label, value]) => (
            <div key={label} className="px-4 py-4">
              <span className="block text-[10.5px] font-semibold uppercase tracking-wide text-muted-foreground">
                {label}
              </span>
              <span className="mt-1.5 block font-mono text-xl font-bold tabular-nums sm:text-2xl">{value}</span>
            </div>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border bg-muted/20 px-4 py-2.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
            Compliance
          </span>
          <ScreenChip label="ADP" state={out.adp} />
          <ScreenChip label="ACP" state="pass" />
          <ScreenChip label="Coverage" state="pass" />
          <ScreenChip label="Top-heavy" state={out.topHeavy} />
          <span className="ml-auto text-xs text-muted-foreground">Plus every employee, line by line.</span>
        </div>
      </div>

      <OutputsPanel />
    </PublicSection>
  )
}

function JulioSection() {
  const authed = usePublicAuthed()

  return (
    <PublicSection id="julio">
      <div className="grid gap-9 lg:grid-cols-[0.95fr_1.05fr] lg:items-start">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">Meet Julio, the AI assistant</h2>
          <p className="mt-3 text-base leading-7 text-muted-foreground">
            Ask for an outcome: the cheapest design that passes ADP, more for the owner, or a new-comparability
            plan. Julio searches against the same screens as the calculator and stages proposals for your review.
          </p>
          <ol className="mt-7 border-t border-border">
            {JULIO_PIPELINE.map(({ n, title, body }) => (
              <li key={n} className="flex gap-3 border-b border-border py-3 text-sm leading-6 text-muted-foreground">
                <span className="shrink-0 pt-0.5 font-mono text-[11px] text-brand">{n}</span>
                <span>
                  <span className="font-semibold text-foreground">{title}</span> {body}
                </span>
              </li>
            ))}
          </ol>
          <div className="mt-6">
            <Button asChild variant="outline">
              <a href={authed ? getStudioHref('/') : getStudioHref('/login')}>
                {authed ? 'Open Julio in the calculator' : 'Log in to try Julio'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
            </Button>
          </div>
        </div>

        {/* Goal-seek card: what a staged optimizer answer actually looks like. */}
        <div className="border border-border bg-card">
          <div className="flex items-center gap-3 border-b border-border px-5 py-4">
            <JulioAvatar className="h-8 w-8 shrink-0" />
            <p className="text-sm font-semibold">“Get the most into the owner’s account.”</p>
          </div>
          <div className="flex flex-col gap-3 p-5">
            <p className="text-[10px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">
              Example · staged into Compare, Plan B–D
            </p>
            {STAGED_DESIGNS.map(({ slot, name, owner, total }) => (
              <div key={slot} className="border border-border bg-background px-4 py-3">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-sm font-semibold">{name}</span>
                  <span className="shrink-0 font-mono text-[11px] text-muted-foreground">{slot}</span>
                </div>
                <p className="mt-1.5 text-[13px] text-muted-foreground">
                  <span className="font-mono font-bold text-brand">{owner}</span> to the owner · {total} total
                  employer
                </p>
              </div>
            ))}
            <p className="flex gap-2 pt-1 text-xs leading-5 text-muted-foreground">
              <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
              <span>
                Each clears the modeled ADP, coverage, and cross-testing screens, and lands as a proposal you
                review and Apply – nothing changes on its own.
              </span>
            </p>
            <p className="text-[11px] leading-5 text-muted-foreground/80">
              Modeled under Retira.app&apos;s informal screens, not a legal minimum. Confirm with the plan&apos;s TPA or
              actuary.
            </p>
          </div>
        </div>
      </div>

      <div className="mt-9 grid gap-4 min-[500px]:grid-cols-2 lg:grid-cols-4">
        {PERSONAS.map(({ icon: Icon, who, what }) => (
          <div
            key={who}
            className="border border-border bg-card p-5 transition-[transform,border-color] hover:-translate-y-0.5 hover:border-brand/60 motion-reduce:transition-none motion-reduce:hover:translate-y-0"
          >
            <Icon className="h-5 w-5 text-brand" aria-hidden="true" />
            <h3 className="mt-3 text-[15px] font-semibold">{who}</h3>
            <p className="mt-2 text-[13px] leading-6 text-muted-foreground">{what}</p>
          </div>
        ))}
      </div>
    </PublicSection>
  )
}

/* Initials for the no-photo avatar; ignores bracketed placeholder tokens. */
function initials(name) {
  return name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z]/.test(w))
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join('')
}

/** Employer cost drifts down while the employee nest egg compounds up. */
function WinWinChart() {
  return (
    <svg viewBox="0 0 240 96" className="mt-4 w-full" aria-hidden="true">
      {[24, 48, 72].map((y) => (
        <line key={y} x1="6" x2="234" y1={y} y2={y} className="stroke-border" strokeDasharray="3 4" />
      ))}
      <path
        d="M8 34C70 37 150 46 232 60"
        fill="none"
        className="stroke-muted-foreground"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
      <path
        d="M8 82C100 79 172 60 226 28"
        fill="none"
        className="stroke-brand"
        strokeWidth="2"
        strokeLinecap="round"
      />
      <ellipse cx="227" cy="25" rx="4.2" ry="5.2" className="fill-brand" />
      <text x="8" y="24" fontSize="9" className="fill-muted-foreground">Employer cost</text>
      <text x="128" y="90" fontSize="9" className="fill-brand">Employee nest egg</text>
    </svg>
  )
}

/** Weeks of proposal cycle vs. minutes in the calculator, as two bars. */
function AboutSection() {
  return (
    <PublicSection id="about" alt>
      {/* Story and the people who built it side by side, so the team reads as
          part of the answer to "why", not a footnote under it. */}
      <div className="grid gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:items-center">
        <div>
          <SectionHead
            eyebrow="About"
            title="Why we built Retira."
          />
          <p className="mt-4 max-w-2xl text-base leading-7 text-muted-foreground">
            Answering a simple plan-design question used to be slow. You would send the census to a TPA, wait
            for a proposal, ask for one change, and wait again, so a week could go by before you knew what a
            design actually cost. The math behind it is not the hard part, though: a computer can price those
            trade-offs in seconds. So we built the calculator we wanted for ourselves, where you type in the
            plan and the numbers update as you go, the compliance checks read in plain English instead of
            jargon, and the census stays on your own machine because it is never uploaded.
          </p>
        </div>

        <div>
          <h3 className="text-base font-semibold">Meet the team</h3>
          <div className="mt-4 grid gap-4 min-[420px]:grid-cols-2">
            {TEAM.map(({ name, role, bio, photo, anonymousGroup }) => (
              <div key={name} className="flex items-center gap-3.5 border border-border bg-background p-4">
                {anonymousGroup ? (
                  <span
                    className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-soft text-brand"
                    aria-hidden="true"
                  >
                    <UsersRound className="h-7 w-7" strokeWidth={1.75} />
                  </span>
                ) : photo ? (
                  <img
                    src={photo}
                    alt={`${name}, ${role} at Retira`}
                    width="64"
                    height="64"
                    className="h-16 w-16 shrink-0 rounded-full object-cover"
                  />
                ) : (
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-brand-soft text-lg font-semibold text-brand">
                    {initials(name)}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-tight">{name}</p>
                  <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-brand">
                    {role}
                  </p>
                  {bio ? <p className="mt-1.5 text-xs leading-5 text-muted-foreground">{bio}</p> : null}
                </div>
              </div>
            ))}
          </div>
          <p className="mt-4 text-xs leading-6 text-muted-foreground">
            {TEAM_NOTE}{' '}
            <Link to="/contact" className="underline underline-offset-4 transition-colors hover:text-foreground">
              Contact us
            </Link>{' '}
            to learn more about the team.
          </p>
        </div>
      </div>

      <div className="mt-9 border border-border bg-background p-6 lg:grid lg:grid-cols-[1fr_0.8fr] lg:items-center lg:gap-8">
        <div>
          <h3 className="text-lg font-semibold leading-snug">A win-win by design.</h3>
          <p className="mt-2 text-sm leading-7 text-muted-foreground">
            Plan design isn&apos;t zero-sum. The designs Retira surfaces cut employer spending and grow
            employee nest eggs at the same time, and show both numbers side by side.
          </p>
        </div>
        <WinWinChart />
      </div>
    </PublicSection>
  )
}

function WhatItModels() {
  return (
    <PublicSection id="what-it-models">
      <div className="grid gap-10 lg:grid-cols-[0.8fr_1.2fr]">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">More than a calculator</h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            Build the census, screen the design, and package the decision, all in one place.
          </p>
        </div>
        <dl className="border-t border-border">
          {SPEC.map(({ icon: Icon, term, desc }) => (
            <div
              key={term}
              className="grid gap-1 border-b border-border py-4 sm:grid-cols-[0.7fr_1.3fr] sm:gap-6"
            >
              <dt className="font-semibold">
                <Icon className="mr-2 inline-block h-4 w-4 align-[-2px] text-brand" aria-hidden="true" />
                {term}
              </dt>
              <dd className="text-sm leading-6 text-muted-foreground">{desc}</dd>
            </div>
          ))}
        </dl>
      </div>

      <div className="mt-9 flex flex-wrap items-center gap-3">
        <PublicPrimaryCta withArrow />
        <Button asChild variant="ghost">
          <Link to="/book" onPointerEnter={prefetchBookPage} onFocus={prefetchBookPage}>
            Book a call
          </Link>
        </Button>
      </div>
    </PublicSection>
  )
}

function Faq() {
  return (
    <section id="faq" className="scroll-mt-20 border-t border-border bg-card">
      <div className="mx-auto grid max-w-6xl gap-8 px-5 py-16 lg:grid-cols-[0.7fr_1.3fr] lg:gap-12">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            Common questions
          </p>
          <h2 className="mt-3 text-2xl font-semibold tracking-tight">A few important details.</h2>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            The calculator is fast by design, with clear limits on what its outputs mean.
          </p>
          <FlightPathMark className="mt-8 hidden w-48 text-muted-foreground/50 lg:block" />
        </div>

        <Accordion type="single" collapsible className="border-t border-border">
          {HOME_FAQS.map(({ id, question, answer, link }) => (
            <AccordionItem key={id} value={id}>
              <AccordionTrigger className="py-4 text-left text-sm sm:text-base">
                {question}
              </AccordionTrigger>
              <AccordionContent className="pb-4 text-sm leading-6 text-muted-foreground">
                {answer}
                {link ? <> <Link to={link.to}>{link.label}</Link></> : null}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  )
}

/* -------------------------------------------------------------------------- */
/* Previews - built to match the calculator's dashboard and charts             */
/* -------------------------------------------------------------------------- */

/**
 * Hero panel: framed as one screen of the actual app (browser chrome + an
 * in-place tab strip – Design / Compare / Charts / Assistant – plus an Export
 * menu), so it reads as an interactive *example* rather than the whole product.
 * Design holds the depth: three interacting levers (match, vesting, auto-enroll)
 * re-roll employer cost, participation, forfeitures, a morphing vesting curve,
 * and the compliance screens. Compare reads that configured plan
 * against a vanilla baseline; Charts plots deferral vs. pay (HCE/NHCE);
 * Assistant is a chat surface that gates on login – all on the sample census.
 */
const HERO_TABS = [
  { id: 'design', label: 'Design', icon: SlidersHorizontal },
  { id: 'compare', label: 'Compare', icon: Columns2 },
  { id: 'charts', label: 'Charts', icon: BarChart3 },
]

// Assistant lives in the right-aligned group beside Export, not with the
// left section tabs.
const ASSISTANT_TAB = { id: 'assistant', label: 'Assistant', icon: Sparkles }

function ComparePreview() {
  const [tab, setTab] = useState('design')
  const [exportOpen, setExportOpen] = useState(false)
  const { matchIdx, vestIdx, autoEnroll, setMatchIdx, setVestIdx, toggleAutoEnroll } = useHeroPlan()
  const match = MATCH_OPTIONS[matchIdx]
  const vesting = VESTING_OPTIONS[vestIdx]
  const out = modelPlan(match, vesting, autoEnroll)

  const cost = useAnimatedNumber(out.cost)
  const part = useAnimatedNumber(out.part)
  const forf = useAnimatedNumber(out.forfeitures)

  const onTab = (t) => setTab(t.id)

  return (
    <div className="overflow-hidden bg-card shadow-sm ring-1 ring-foreground/10">
      {/* Browser chrome – signals this is a real screen of the app, not a flat card. */}
      <div className="flex items-center gap-3 border-b border-border/60 bg-muted/40 px-3 py-2.5">
        <span className="flex shrink-0 gap-1.5" aria-hidden="true">
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
          <span className="h-2.5 w-2.5 rounded-full bg-foreground/15" />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-1.5 border border-border/60 bg-background px-2.5 py-1 text-[11px] text-muted-foreground">
          <ShieldCheck className="h-3 w-3 shrink-0 text-emerald-600" aria-hidden="true" />
          <span className="truncate">studio.retira.app/demo</span>
          <span className="ml-auto hidden shrink-0 text-muted-foreground/70 sm:inline">sample plan · {EXAMPLE_EMPLOYEE_COUNT} employees</span>
        </span>
      </div>

      {/* Section tabs switch the screen in place; Export reveals its formats.
          Labels collapse to icons on phones so the strip never overflows. */}
      <div className="flex items-stretch gap-0.5 border-b border-border/60 bg-muted/20 px-1.5 sm:px-2">
        {HERO_TABS.map((t) => {
          const { id, label, icon: Icon } = t
          const isActive = tab === id
          return (
            <button
              key={id}
              type="button"
              onClick={() => onTab(t)}
              aria-label={label}
              aria-current={isActive ? 'page' : undefined}
              className={`inline-flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-xs transition-colors sm:px-3 ${
                isActive
                  ? 'border-primary font-semibold text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span className="hidden sm:inline">{label}</span>
            </button>
          )
        })}

        <div className="ml-auto flex items-stretch gap-0.5">
          {(() => {
            const { id, label, icon: Icon } = ASSISTANT_TAB
            const isActive = tab === id
            return (
              <button
                type="button"
                onClick={() => onTab(ASSISTANT_TAB)}
                aria-label={label}
                aria-current={isActive ? 'page' : undefined}
                className={`inline-flex items-center gap-1.5 border-b-2 px-2.5 py-2 text-xs transition-colors sm:px-3 ${
                  isActive
                    ? 'border-primary font-semibold text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="hidden sm:inline">{label}</span>
              </button>
            )
          })()}

          <div className="relative flex items-center">
          <button
            type="button"
            onClick={() => setExportOpen((o) => !o)}
            aria-expanded={exportOpen}
            aria-label="Export"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
          >
            <Download className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span className="hidden sm:inline">Export</span>
          </button>
          {exportOpen ? (
            <>
              <button
                type="button"
                aria-hidden="true"
                tabIndex={-1}
                onClick={() => setExportOpen(false)}
                className="fixed inset-0 z-10 cursor-default"
              />
              <div className="absolute right-0 top-full z-20 mt-1 w-44 border border-border bg-card p-1 shadow-md">
                <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-muted-foreground">Export this plan</p>
                <span className="flex items-center gap-2 px-2 py-1.5 text-xs text-foreground">
                  <FileUp className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  CSV of any selection
                </span>
                <span className="flex items-center gap-2 px-2 py-1.5 text-xs text-foreground">
                  <BarChart3 className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  PDF plan summary
                </span>
                <button
                  type="button"
                  className="flex w-full items-center gap-2 px-2 py-1.5 text-xs text-foreground"
                >
                  <FileCode2 className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                  HTML page
                </button>
              </div>
            </>
          ) : null}
          </div>
        </div>
      </div>

      {/* Active screen */}
      <div className="min-h-[224px] p-3 sm:p-4">
        {tab === 'design' ? (
          <DesignTab
            match={match}
            vesting={vesting}
            matchIdx={matchIdx}
            vestIdx={vestIdx}
            autoEnroll={autoEnroll}
            setMatchIdx={setMatchIdx}
            setVestIdx={setVestIdx}
            setAutoEnroll={toggleAutoEnroll}
            cost={cost}
            part={part}
            forf={forf}
          />
        ) : tab === 'compare' ? (
          <CompareTab planB={{ cost, part, forf }} />
        ) : tab === 'charts' ? (
          <ChartsTab />
        ) : (
          <AssistantTab />
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-border/60 bg-muted/20 px-4 py-2.5">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
          Compliance
        </span>
        <ScreenChip label="ADP" state={out.adp} />
        <ScreenChip label="Top-heavy" state={out.topHeavy} />
        <span className="ml-auto inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <RefreshCw className="h-3 w-3 shrink-0 text-primary" aria-hidden="true" />
          Recalculated in your browser
        </span>
      </div>
    </div>
  )
}

/* ---- hero tab screens ---- */

function DesignTab({ match, vesting, matchIdx, vestIdx, autoEnroll, setMatchIdx, setVestIdx, setAutoEnroll, cost, part, forf }) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      <div className="space-y-3">
        <Segmented label="Employer match" note={match.note} options={MATCH_OPTIONS} activeIdx={matchIdx} onSelect={setMatchIdx} />
        <Segmented label="Vesting schedule" note={vesting.note} options={VESTING_OPTIONS} activeIdx={vestIdx} onSelect={setVestIdx} />
        <button
          type="button"
          role="switch"
          aria-checked={autoEnroll}
          onClick={() => setAutoEnroll()}
          className="flex w-full items-center justify-between gap-2 border border-border bg-background px-3 py-2 text-left transition-colors hover:bg-muted/30"
        >
          <span className="min-w-0">
            <span className="block text-xs font-semibold text-foreground">Auto-enroll new hires</span>
            <span className="block text-[11px] text-muted-foreground">6% default · auto-escalate</span>
          </span>
          <span
            aria-hidden="true"
            className={`relative h-5 w-9 shrink-0 rounded-full transition-colors ${
              autoEnroll ? 'bg-primary' : 'bg-muted-foreground/30'
            }`}
          >
            <span
              className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${
                autoEnroll ? 'translate-x-[18px] dark:bg-primary-foreground' : 'translate-x-0.5'
              }`}
            />
          </span>
        </button>
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-3 divide-x divide-border border border-border">
          <Metric label="Cost / yr" value={fmtM(cost)} />
          <Metric label="Participating" value={`${Math.round(part)}%`} />
          <Metric label="Forfeit. recap." value={fmtK(forf)} />
        </div>
        <VestingCurve schedule={vesting.schedule} />
      </div>
    </div>
  )
}

/** Reads the configured plan (Plan B) against a vanilla baseline (Plan A). */
function CompareTab({ planB }) {
  const rows = [
    { label: 'Employer cost / yr', a: fmtM(BASELINE.cost), b: fmtM(planB.cost), delta: signed(planB.cost - BASELINE.cost, (n) => '$' + Math.abs(n).toFixed(2) + 'M') },
    { label: 'Participating', a: `${BASELINE.part}%`, b: `${Math.round(planB.part)}%`, delta: signed(planB.part - BASELINE.part, (n) => Math.abs(Math.round(n)) + ' pts') },
    { label: 'Forfeit. recaptured', a: fmtK(BASELINE.forfeitures), b: fmtK(planB.forf), delta: signed(planB.forf - BASELINE.forfeitures, (n) => '$' + Math.abs(n).toFixed(1) + 'K') },
  ]
  return (
    <div className="border border-border">
      <div className="grid grid-cols-[1.4fr_0.8fr_0.8fr_0.7fr] gap-x-2 border-b border-border bg-muted/20 px-3 py-2 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        <span>Metric</span>
        <span className="text-right">Plan A</span>
        <span className="text-right">Plan B</span>
        <span className="text-right">Δ</span>
      </div>
      <p className="grid grid-cols-[1.4fr_0.8fr_0.8fr_0.7fr] gap-x-2 px-3 pt-2 text-[10px] text-muted-foreground">
        <span>&nbsp;</span>
        <span className="text-right">Baseline</span>
        <span className="text-right">Your design</span>
        <span className="text-right">&nbsp;</span>
      </p>
      {rows.map((r) => (
        <div key={r.label} className="grid grid-cols-[1.4fr_0.8fr_0.8fr_0.7fr] items-baseline gap-x-2 px-3 py-2.5">
          <span className="text-xs text-muted-foreground">{r.label}</span>
          <span className="text-right font-mono text-xs text-muted-foreground tabular-nums">{r.a}</span>
          <span className="text-right font-mono text-xs font-semibold text-foreground tabular-nums">{r.b}</span>
          <span className={`text-right font-mono text-xs tabular-nums ${r.delta.tone}`}>{r.delta.text}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Charts tab: a real tab pane (no scroll hijack) holding tiny non-interactive
 * previews of the three demographics charts, plus an explicit jump to the
 * full, live versions further down the page – which stay down there so the
 * card never has to cram them in.
 */
function ChartsTab() {
  const goToCharts = () => {
    const el = document.getElementById('outputs')
    if (!el) return
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' })
  }

  return (
    <div className="flex min-h-[200px] flex-col gap-3">
      <div className="grid grid-cols-3 gap-2">
        <MiniChartCard title="Comp vs. deferral">
          <svg viewBox="0 0 100 64" className="w-full" aria-hidden="true">
            {SCATTER_POINTS.filter((_, i) => i % 6 === 0).map((p, i) => (
              <circle key={i} cx={4 + p.x * 0.92} cy={60 - p.y * 0.56} r="2.1" fill={p.color} fillOpacity="0.85" />
            ))}
          </svg>
        </MiniChartCard>
        <MiniChartCard title="By age & source">
          <svg viewBox="0 0 100 64" className="w-full" aria-hidden="true">
            {AGE_CONTRIB.map((b, i) => {
              let y = 62
              return b.seg.map((v, si) => {
                if (v <= 0) return null
                const h = (v / 30) * 58
                y -= h
                return <rect key={`${i}-${si}`} x={4 + i * 16} y={y} width="12" height={h} fill={CONTRIB_SEGMENTS[si][1]} />
              })
            })}
          </svg>
        </MiniChartCard>
        <MiniChartCard title="Age × gender">
          <svg viewBox="0 0 100 64" className="w-full" aria-hidden="true">
            {HEATMAP.slice(1).map((row, ri) =>
              [row.m[0], row.f[0]].map((v, ci) => (
                <rect
                  key={`${ri}-${ci}`}
                  x={4 + ri * 19}
                  y={8 + ci * 26}
                  width="17"
                  height="24"
                  fill={heatColor(v)}
                />
              ))
            )}
          </svg>
        </MiniChartCard>
      </div>

      <p className="text-[11px] leading-5 text-muted-foreground">
        The full versions are live on this page – click any chart, or drag across the scatter, to pull up
        the employees behind your selection.
      </p>

      <div className="mt-auto">
        <Button size="sm" onClick={goToCharts}>
          Open the full charts
          <ArrowDown className="ml-2 h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </div>
    </div>
  )
}

function MiniChartCard({ title, children }) {
  return (
    <div className="min-w-0 border border-border bg-background p-2">
      <p className="truncate text-[10px] font-medium text-muted-foreground">{title}</p>
      <div className="mt-1">{children}</div>
    </div>
  )
}

/**
 * Assistant tab: a chat surface that gates the moment you try to use it –
 * focusing the box, picking a suggestion, or sending all reveal a login CTA,
 * since the real assistant works on your saved plans.
 */
function AssistantTab() {
  const [gated, setGated] = useState(false)
  const loginHref = getStudioHref('/login')
  const suggestions = [
    'Cut employer cost without losing participation?',
    'Who isn’t capturing the full match?',
    'Is this plan at risk on the ADP screen?',
  ]

  return (
    <div className="flex min-h-[200px] flex-col gap-3">
      <div className="flex gap-2">
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <Sparkles className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
        </span>
        <div className="border border-border bg-muted/30 px-3 py-2 text-xs leading-5 text-foreground">
          Ask about this plan and census in plain English – I read the same numbers the calculator does.
        </div>
      </div>

      {gated ? (
        <div className="flex flex-col items-start gap-2 border border-primary/30 bg-primary/5 p-3">
          <p className="text-xs text-foreground">
            Log in to chat with the assistant – it works on your own saved plans.
          </p>
          <Button asChild size="sm">
            <a href={loginHref}>Log in to chat</a>
          </Button>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {suggestions.map((q) => (
            <button
              key={q}
              type="button"
              onClick={() => setGated(true)}
              className="border border-border bg-background px-2 py-1 text-left text-[11px] text-muted-foreground transition-colors hover:text-foreground"
            >
              {q}
            </button>
          ))}
        </div>
      )}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          setGated(true)
        }}
        className="mt-auto flex gap-2"
      >
        <input
          type="text"
          placeholder="Ask about cost, participation, compliance…"
          onFocus={() => setGated(true)}
          className="min-w-0 flex-1 border border-border bg-background px-3 py-2 text-xs outline-none placeholder:text-muted-foreground focus:border-primary"
        />
        <button
          type="submit"
          aria-label="Send message"
          className="inline-flex items-center justify-center bg-primary px-3 text-primary-foreground transition-opacity hover:opacity-90"
        >
          <ArrowRight className="h-4 w-4" aria-hidden="true" />
        </button>
      </form>
    </div>
  )
}

// Format a delta with a leading sign and a tone class (green up, muted flat).
function signed(value, fmt) {
  if (Math.abs(value) < 0.005) return { text: '–', tone: 'text-muted-foreground' }
  const sign = value > 0 ? '+' : '−'
  return { text: sign + fmt(value), tone: value > 0 ? 'text-emerald-700' : 'text-amber-700' }
}

function Segmented({ label, note, options, activeIdx, onSelect }) {
  return (
    <div>
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
        <span className="truncate text-[11px] text-muted-foreground">{note}</span>
      </div>
      <div className="mt-1.5 grid grid-cols-3 gap-1">
        {options.map((o, i) => {
          const isActive = i === activeIdx
          return (
            <button
              key={o.id}
              type="button"
              onClick={() => onSelect(i)}
              aria-pressed={isActive}
              className={`px-1.5 py-1.5 text-[11px] font-medium leading-tight transition-colors ${
                isActive
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-border bg-background text-muted-foreground hover:text-foreground'
              }`}
            >
              {o.label}
            </button>
          )
        })}
      </div>
    </div>
  )
}

function Metric({ label, value }) {
  return (
    <div className="px-2.5 py-2.5">
      <p className="truncate text-[10px] uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 font-mono text-base font-semibold tracking-tight text-foreground tabular-nums">{value}</p>
    </div>
  )
}

/** Bars of % vested by year of service; heights morph as the schedule changes. */
function VestingCurve({ schedule }) {
  return (
    <div className="border border-border p-3">
      <div className="flex items-baseline justify-between">
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">% vested by year</span>
        <span className="text-[10px] text-muted-foreground">yrs of service</span>
      </div>
      <div className="mt-2 flex h-16 items-end gap-1.5">
        {schedule.map((pct, i) => (
          <div key={i} className="flex h-full flex-1 items-end" title={`Year ${i + 1}: ${pct}% vested`}>
            <div
              className="w-full bg-primary/80 transition-[height] duration-500 ease-out"
              style={{ height: `${Math.max(pct, 4)}%` }}
            />
          </div>
        ))}
      </div>
      <div className="mt-1 flex gap-1.5">
        {schedule.map((_, i) => (
          <span key={i} className="flex-1 text-center text-[9px] text-muted-foreground">
            {i + 1}
          </span>
        ))}
      </div>
    </div>
  )
}

function ScreenChip({ label, state }) {
  const ok = state !== 'watch'
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-foreground">
      <span
        aria-hidden="true"
        className={`h-1.5 w-1.5 rounded-full ${ok ? 'bg-emerald-600' : 'bg-amber-500'}`}
      />
      {label}
      <span className="text-muted-foreground">{state}</span>
    </span>
  )
}

/** Outputs showcase: a faux pane with the strongest charts from the app. */
const EMPTY_SET = new Set()
const fmtUSD = (n) => '$' + Math.round(n).toLocaleString('en-US')

function OutputsPanel() {
  // One selection at a time across the three charts, so picking in one clears
  // the others and a single summary + CTA shows underneath.
  const [sel, setSel] = useState(null) // { chart, keys: Set }
  const summaryRef = useRef(null)
  const updateSelection = (chart, keys) => setSel(keys && keys.size ? { chart, keys } : null)
  // Completed selections (click / finished drag) scroll the summary into view;
  // live drag previews via onPreview only update state.
  const select = (chart) => (keys) => {
    updateSelection(chart, keys)
    if (!keys?.size) return

    window.requestAnimationFrame(() => {
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      summaryRef.current?.scrollIntoView({
        behavior: reduceMotion ? 'auto' : 'smooth',
        block: 'center',
      })
    })
  }
  const stats = sel ? computeSelectionStats(sel) : null

  return (
    <div className="mt-10 bg-card ring-1 ring-foreground/10">
      <div className="flex items-center gap-2 border-b border-border/60 bg-primary/5 px-4 py-2.5 text-xs leading-5 text-foreground">
        <MousePointer2 className="h-4 w-4 shrink-0 text-primary" />
        <span>
          <span className="font-medium">Click any chart</span> to select employees · Shift-click to combine · drag
          across the scatter to box-select. We&apos;ll take you to the summary below.
        </span>
      </div>

      <div className="grid gap-4 p-4 md:grid-cols-2">
        <ChartCard
          className="md:col-span-2"
          title="Compensation vs. deferral rate"
          description="Each dot is one employee · HCE vs. NHCE · click, Shift-click, or drag to box-select"
        >
          <CompDeferralScatter
            selected={sel?.chart === 'scatter' ? sel.keys : EMPTY_SET}
            onSelect={select('scatter')}
            onPreview={(keys) => updateSelection('scatter', keys)}
          />
        </ChartCard>

        <ChartCard
          title="Contributions by age"
          description="Average employee contribution, by source · click, Shift-click, or drag to select"
        >
          <ContributionsByAge
            selected={sel?.chart === 'bars' ? sel.keys : EMPTY_SET}
            onSelect={select('bars')}
            onPreview={(keys) => updateSelection('bars', keys)}
          />
        </ChartCard>

        <ChartCard
          title="Avg deferral by age & gender"
          description="Mean elected deferral rate · click, Shift-click, or drag to select"
        >
          <DeferralHeatmap
            selected={sel?.chart === 'heatmap' ? sel.keys : EMPTY_SET}
            onSelect={select('heatmap')}
            onPreview={(keys) => updateSelection('heatmap', keys)}
          />
        </ChartCard>
      </div>

      <div ref={summaryRef} className="scroll-mt-20 border-t border-border/60 p-4">
        <SelectionSummary stats={stats} onClear={stats ? () => setSel(null) : null} />
      </div>
    </div>
  )
}

// Hoisted so OutputsPanel can call it while the data lives further down.
function computeSelectionStats({ chart, keys }) {
  if (chart === 'scatter') {
    const pts = [...keys].map((i) => SCATTER_POINTS[i]).filter(Boolean)
    const n = pts.length || 1
    const avgComp = pts.reduce((s, p) => s + SCATTER_COMP_MIN + (p.x / 100) * SCATTER_COMP_RANGE, 0) / n
    const avgDef = pts.reduce((s, p) => s + (p.y / 100) * SCATTER_DEFERRAL_MAX, 0) / n
    return [
      ['Employees', String(pts.length)],
      ['Avg compensation', fmtUSD(avgComp)],
      ['Avg deferral', `${avgDef.toFixed(1)}%`],
    ]
  }

  if (chart === 'bars') {
    const bands = AGE_CONTRIB.filter((b) => keys.has(b.band))
    const emp = bands.reduce((s, b) => s + b.count, 0)
    const totalK = bands.reduce((s, b) => s + b.seg.reduce((a, c) => a + c, 0) * b.count, 0)
    const avg = emp ? (totalK / emp) * 1000 : 0
    return [
      ['Employees', String(emp)],
      ['Age bands', String(bands.length)],
      ['Avg contribution', fmtUSD(avg)],
    ]
  }

  const cells = []
  HEATMAP.forEach((r) => {
    if (keys.has(`${r.band}|M`)) cells.push(r.m)
    if (keys.has(`${r.band}|F`)) cells.push(r.f)
  })
  const emp = cells.reduce((s, c) => s + c[1], 0)
  const avgDef = emp ? cells.reduce((s, c) => s + c[0] * c[1], 0) / emp : 0
  return [
    ['Employees', String(emp)],
    ['Groups', String(cells.length)],
    ['Avg deferral', `${avgDef.toFixed(1)}%`],
  ]
}

function SelectionSummary({ stats, onClear }) {
  const isEmpty = !stats
  return (
    <div className="border border-border bg-muted/20 p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <p className="text-sm font-medium">Your selection</p>
        {!isEmpty && (
          <button
            type="button"
            onClick={onClear}
            className="text-xs text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground"
          >
            Clear
          </button>
        )}
      </div>
      {isEmpty ? (
        <p className="text-sm text-muted-foreground">
          Nothing selected yet. Click a chart, or drag across the scatter, and the numbers show up here.
        </p>
      ) : (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {stats.map(([label, value]) => (
            <div key={label} className="border border-border bg-background px-3 py-2">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
              <div className="font-mono text-base font-semibold">{value}</div>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-xs leading-6 text-muted-foreground">
        On Retira.app, a selection like this opens the full list of employees behind it:
        names, compensation, deferrals, employer contributions, compliance flags, and one-click CSV export.
      </p>
      <p className="mt-2 text-xs leading-6 text-muted-foreground">
        Identifiers stay hidden by default (you&apos;ll just see Employee 1, Employee 2, and so on), and you can
        reveal names and IDs whenever you actually need them.
      </p>
      <Button asChild size="sm" className="mt-2">
        <Link to="/demo">
          See it in the demo
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </Button>
    </div>
  )
}

/* ---- summary primitives ---- */

function ChartCard({ title, description, children, className = '' }) {
  return (
    <Card className={`min-w-0 ${className}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold">{title}</CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent className="pt-0">{children}</CardContent>
    </Card>
  )
}

function Legend({ items }) {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
      {items.map(([label, color]) => (
        <span key={label} className="inline-flex items-center gap-1.5">
          <span className="h-2.5 w-2.5 shrink-0" style={{ backgroundColor: color }} />
          {label}
        </span>
      ))}
    </div>
  )
}

/* ---- flagship charts ---- */

// Positions are pre-baked in 0–100 plot space (x = comp, y = deferral %).
const SCATTER_COMP_MIN = 44000
const SCATTER_COMP_RANGE = 201000
const SCATTER_DEFERRAL_MAX = 17.02127659574468
const NHCE_POINTS = [
  [13.9, 35.3], [50.2, 88.1], [32.8, 47], [7, 35.3], [45.3, 58.8], [21.9, 0],
  [4, 17.6], [41.8, 70.5], [15.9, 0], [48.8, 58.8], [10, 47], [55.2, 70.5],
  [23.9, 47], [9.7, 0], [36.8, 70.5], [18.9, 29.4], [51.7, 70.5], [26.9, 52.9],
  [39.8, 47], [11.4, 0], [56.7, 70.5], [5, 23.5], [43.8, 70.5], [16.7, 41.1],
  [49.5, 58.8], [30.8, 58.8], [23.1, 0], [1, 23.5], [7.5, 29.4], [35.8, 47],
  [24.4, 0], [53.7, 64.6], [8.5, 47], [42.7, 58.8], [28.9, 58.8], [14.9, 35.3],
  [33.3, 52.9], [52.2, 76.4], [19.4, 52.9], [17.4, 5.9], [38.3, 47], [12.4, 35.3],
  [40.8, 52.9], [45.8, 64.6], [26.4, 58.8], [11.9, 29.4], [6.5, 35.3], [37.6, 52.9],
  [54.7, 58.8], [20.9, 58.8], [13.4, 0], [32.3, 58.8], [36.4, 52.6], [27.4, 47],
  [9, 29.4], [10, 35.3], [44.8, 70.5], [19.9, 58.8], [33.8, 5.9], [18.9, 29.4],
  [51.2, 70.5], [13.4, 35.3], [39.3, 70.5], [0, 17.6], [41.3, 47], [8, 0],
  [57.6, 70.5], [28.4, 52.9], [8, 29.4], [47.3, 58.8], [14.3, 41.1], [55.7, 64.6],
  [5.5, 17.6],
]
const HCE_POINTS = [
  [70.1, 58.8], [87.6, 65.4], [60.2, 71.2], [65.2, 88.1], [75.1, 88.1], [74.1, 0],
  [82.6, 88.1], [93.5, 62], [61.7, 82.3], [65.7, 68.4], [80.1, 76.4], [79.1, 0],
  [100, 74.3], [66.7, 88.1], [58.7, 65.3], [85.1, 64.6], [71.6, 100], [62.2, 0],
  [75.6, 88.1], [96.5, 80.2], [67.2, 0], [90, 65.3],
]
const SCATTER_POINTS = [
  ...NHCE_POINTS.map(([x, y]) => ({ x, y, color: C.nhce })),
  ...HCE_POINTS.map(([x, y]) => ({ x, y, color: C.hce })),
]

/* ---- shared box / lasso drag selection for the demo charts ---- */

function pointInPoly(point, poly) {
  let inside = false
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i]
    const b = poly[j]
    if (((a.y > point.y) !== (b.y > point.y))
      && point.x < ((b.x - a.x) * (point.y - a.y)) / ((b.y - a.y) || Number.EPSILON) + a.x) inside = !inside
  }
  return inside
}

function shapeHasPoint(shape, p) {
  if (shape.type === 'lasso') return pointInPoly(p, shape.points)
  const r = shape.rect
  return p.x >= r.x && p.x <= r.x + r.width && p.y >= r.y && p.y <= r.y + r.height
}

function shapeHitsRect(shape, r) {
  if (shape.type === 'lasso') return pointInPoly({ x: r.x + r.width / 2, y: r.y + r.height / 2 }, shape.points)
  const s = shape.rect
  return r.x <= s.x + s.width && r.x + r.width >= s.x && r.y <= s.y + s.height && r.y + r.height >= s.y
}

// Selectable DOM elements carry data-select-key; return the keys a shape covers.
function domKeysForShape(shape, el) {
  const b = el.getBoundingClientRect()
  const keys = new Set()
  el.querySelectorAll('[data-select-key]').forEach((node) => {
    const n = node.getBoundingClientRect()
    const r = { x: n.left - b.left, y: n.top - b.top, width: n.width, height: n.height }
    if (shapeHitsRect(shape, r)) keys.add(node.getAttribute('data-select-key'))
  })
  return keys
}

/**
 * Box / lasso drag selection shared by the demo charts. A plain click still
 * reaches the underlying element (or pickAt); dragging past a threshold draws
 * the active shape, and Shift adds to the selection that existed at drag start.
 * Touch keeps native scrolling and tap-to-select.
 */
function useChartShapeSelect({ selected, onPreview, onCommit, getKeys, pickAt }) {
  const plotRef = useRef(null)
  const [tool, setTool] = useState('rectangle')
  const [shape, setShape] = useState(null)
  const toolRef = useRef('rectangle')
  toolRef.current = tool
  const cbRef = useRef({})
  cbRef.current = { selected, onPreview, onCommit, getKeys, pickAt }

  useEffect(() => {
    const el = plotRef.current
    if (!el) return
    const THRESHOLD = 5
    const st = { active: false, pending: false, sx: 0, sy: 0, pid: null, additive: false, points: [], base: EMPTY_SET }
    let suppress = false
    let suppressTimer = null

    const xy = (e) => {
      const b = el.getBoundingClientRect()
      return { x: Math.max(0, Math.min(e.clientX - b.left, b.width)), y: Math.max(0, Math.min(e.clientY - b.top, b.height)) }
    }
    const shapeFor = (cx, cy) => {
      if (toolRef.current === 'lasso') {
        const prev = st.points[st.points.length - 1]
        if (!prev || Math.hypot(cx - prev.x, cy - prev.y) >= 3) st.points.push({ x: cx, y: cy })
        return { type: 'lasso', points: st.points }
      }
      return { type: 'rectangle', rect: { x: Math.min(st.sx, cx), y: Math.min(st.sy, cy), width: Math.abs(cx - st.sx), height: Math.abs(cy - st.sy) } }
    }
    const merge = (keys) => (st.additive ? new Set([...st.base, ...keys]) : keys)

    function onClickCapture(e) { if (!suppress) return; suppress = false; e.preventDefault(); e.stopPropagation() }
    function suppressClick() {
      suppress = true
      if (suppressTimer) clearTimeout(suppressTimer)
      suppressTimer = setTimeout(() => { suppress = false; suppressTimer = null }, 0)
    }
    function down(e) {
      if (e.button != null && e.button !== 0) return
      if (e.target?.closest?.('[data-chart-control]')) return
      const b = el.getBoundingClientRect()
      st.active = false
      st.pending = true
      st.sx = e.clientX - b.left
      st.sy = e.clientY - b.top
      st.pid = e.pointerId
      st.additive = e.shiftKey
      st.points = [{ x: st.sx, y: st.sy }]
      st.base = e.shiftKey ? new Set(cbRef.current.selected) : EMPTY_SET
      document.addEventListener('pointermove', move, { passive: false })
      document.addEventListener('pointerup', up)
    }
    function move(e) {
      if (!st.pending && !st.active) return
      if (e.pointerType === 'touch') return
      const { x, y } = xy(e)
      if (st.pending && !st.active) {
        if (Math.hypot(x - st.sx, y - st.sy) < THRESHOLD) return
        st.pending = false
        st.active = true
        try { el.setPointerCapture(st.pid) } catch { /* ok */ }
      }
      if (!st.active) return
      e.preventDefault()
      const sh = shapeFor(x, y)
      setShape(sh.type === 'lasso' ? { type: 'lasso', points: [...sh.points] } : sh)
      cbRef.current.onPreview?.(merge(cbRef.current.getKeys(sh, el)))
    }
    function up(e) {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      try { el.releasePointerCapture(st.pid) } catch { /* ok */ }
      if (st.active) {
        suppressClick()
        const { x, y } = xy(e)
        cbRef.current.onCommit(merge(cbRef.current.getKeys(shapeFor(x, y), el)))
      } else if (cbRef.current.pickAt) {
        cbRef.current.onCommit(cbRef.current.pickAt({ x: st.sx, y: st.sy }, st.additive, st.base, el))
      }
      st.active = false
      st.pending = false
      setShape(null)
    }
    function cancel() {
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
      st.active = false
      st.pending = false
      setShape(null)
    }
    function key(e) { if (e.key === 'Escape' && st.active) cancel() }

    el.addEventListener('pointerdown', down, true)
    el.addEventListener('click', onClickCapture, true)
    el.addEventListener('pointercancel', cancel)
    document.addEventListener('keydown', key)
    return () => {
      if (suppressTimer) clearTimeout(suppressTimer)
      el.removeEventListener('pointerdown', down, true)
      el.removeEventListener('click', onClickCapture, true)
      el.removeEventListener('pointercancel', cancel)
      document.removeEventListener('keydown', key)
      document.removeEventListener('pointermove', move)
      document.removeEventListener('pointerup', up)
    }
  }, [])

  return { plotRef, tool, setTool, shape }
}

// Rendered in a row above each chart, outside the draggable plot.
function ShapeToggle({ tool, setTool }) {
  const btn = (active) => [
    'flex h-6 w-7 items-center justify-center transition-colors',
    active ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
  ].join(' ')
  return (
    <div className="mb-1 flex justify-end">
      <div data-chart-control="true" className="flex items-center border border-border" role="group" aria-label="Selection shape">
        <button type="button" className={btn(tool === 'rectangle')} aria-pressed={tool === 'rectangle'} title="Box select (drag)" onClick={() => setTool('rectangle')}>
          <BoxSelect className="h-3 w-3" />
        </button>
        <button type="button" className={btn(tool === 'lasso')} aria-pressed={tool === 'lasso'} title="Lasso select (drag a loop)" onClick={() => setTool('lasso')}>
          <LassoSelect className="h-3 w-3" />
        </button>
      </div>
    </div>
  )
}

function ShapeOverlay({ shape }) {
  if (!shape) return null
  if (shape.type === 'lasso') {
    if (shape.points.length < 2) return null
    return (
      <svg aria-hidden="true" className="pointer-events-none absolute inset-0 z-[5] h-full w-full overflow-visible">
        <polygon points={shape.points.map((p) => `${p.x},${p.y}`).join(' ')} fill={SELECTION_COLORS.fill12} stroke={SELECTION_COLORS.stroke60} strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    )
  }
  const { rect } = shape
  return (
    <div aria-hidden="true" className="pointer-events-none absolute z-[5]" style={{ left: rect.x, top: rect.y, width: rect.width, height: rect.height, background: SELECTION_COLORS.fill12, border: `1.5px solid ${SELECTION_COLORS.stroke60}`, borderRadius: 2 }} />
  )
}

/**
 * Scatter of every employee, colored HCE / NHCE, with box / lasso drag select
 * that mirrors the calculator (Shift adds, plain click picks one point).
 */
function CompDeferralScatter({ selected, onSelect, onPreview }) {
  const getKeys = (shape, el) => {
    const b = el.getBoundingClientRect()
    const keys = new Set()
    SCATTER_POINTS.forEach((p, i) => {
      const c = { x: (p.x / 100) * b.width, y: b.height - (p.y / 100) * b.height }
      if (shapeHasPoint(shape, c)) keys.add(i)
    })
    return keys
  }
  const pickAt = (start, additive, base, el) => {
    const b = el.getBoundingClientRect()
    let hit = -1
    let best = 12
    SCATTER_POINTS.forEach((p, i) => {
      const c = { x: (p.x / 100) * b.width, y: b.height - (p.y / 100) * b.height }
      const d = Math.hypot(c.x - start.x, c.y - start.y)
      if (d <= best) { best = d; hit = i }
    })
    if (additive) {
      const next = new Set(base)
      if (hit >= 0) { if (next.has(hit)) next.delete(hit); else next.add(hit) }
      return next
    }
    return hit >= 0 ? new Set([hit]) : EMPTY_SET
  }
  const { plotRef, tool, setTool, shape } = useChartShapeSelect({ selected, onPreview, onCommit: onSelect, getKeys, pickAt })

  return (
    <div>
      <ShapeToggle tool={tool} setTool={setTool} />
      <div className="flex gap-2">
        <span className="w-8 shrink-0 self-center text-center text-[10px] leading-tight text-muted-foreground">
          Deferral %
        </span>
        <div
          ref={plotRef}
          className="relative flex-1 select-none"
          style={{ height: 280, cursor: 'crosshair', touchAction: 'pan-y' }}
        >
          <div className="absolute inset-0 flex flex-col justify-between" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="border-t border-dashed border-border/70" />
            ))}
          </div>
          <div className="absolute inset-0 flex justify-between" aria-hidden="true">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="border-l border-dashed border-border/70" />
            ))}
          </div>

          {SCATTER_POINTS.map((p, i) => {
            const isSel = selected.has(i)
            return (
              <span
                key={i}
                className="pointer-events-none absolute h-[7px] w-[7px] -translate-x-1/2 translate-y-1/2 rounded-full"
                style={{
                  left: `${p.x}%`,
                  bottom: `${p.y}%`,
                  backgroundColor: p.color,
                  opacity: selected.size && !isSel ? 0.3 : 0.9,
                  boxShadow: isSel ? `0 0 0 3px ${SELECTION_COLORS.ring35}` : 'none',
                }}
              />
            )
          })}

          <ShapeOverlay shape={shape} />

          {selected.size > 0 && (
            <div className="pointer-events-none absolute bottom-2 left-2 border border-border bg-background/95 px-2 py-1 text-[11px] font-medium shadow-sm">
              {selected.size} employee{selected.size === 1 ? '' : 's'} selected
            </div>
          )}
        </div>
      </div>
      <div className="mt-2 pl-10 text-center text-[10px] text-muted-foreground">Compensation →</div>
      <Legend items={[['NHCE', C.nhce], ['HCE', C.hce]]} />
    </div>
  )
}

const AGE_CONTRIB = [
  { band: '<25', seg: [0, 0, 0], count: 0 },
  { band: '25–34', seg: [2.5, 1, 0], count: 28 },
  { band: '35–44', seg: [5.7, 3, 0], count: 28 },
  { band: '45–54', seg: [11.3, 3.1, 0], count: 19 },
  { band: '55–64', seg: [20.7, 1.4, 1.8], count: 15 },
  { band: '65+', seg: [22.7, 3.1, 3.5], count: 5 },
]
const CONTRIB_SEGMENTS = [
  ['Pre-tax', C.primary],
  ['Roth', C.violet],
  ['Catch-up', C.success],
]

/** Stacked bars: pre-tax / Roth / catch-up by age band. Click or drag to select. */
function ContributionsByAge({ selected, onSelect, onPreview }) {
  const max = Math.max(...AGE_CONTRIB.map((b) => b.seg.reduce((a, c) => a + c, 0)))
  const hasSel = selected.size > 0
  const pick = (band, additive) => {
    if (!additive) {
      onSelect(new Set([band]))
      return
    }
    const next = new Set(selected)
    if (next.has(band)) next.delete(band)
    else next.add(band)
    onSelect(next)
  }
  const { plotRef, tool, setTool, shape } = useChartShapeSelect({ selected, onPreview, onCommit: onSelect, getKeys: domKeysForShape })

  return (
    <div>
      <ShapeToggle tool={tool} setTool={setTool} />
      <div ref={plotRef} className="relative select-none" style={{ height: 200, touchAction: 'pan-y' }}>
        <div className="absolute inset-0 flex flex-col justify-between" aria-hidden="true">
          {[0, 1, 2, 3, 4].map((i) => (
            <div key={i} className="border-t border-dashed border-border/70" />
          ))}
        </div>
        <div className="absolute inset-0 flex items-end gap-2 px-0.5">
          {AGE_CONTRIB.map((b) => {
            const total = b.seg.reduce((a, c) => a + c, 0)
            const topIndex = b.seg.reduce((acc, v, i) => (v > 0 ? i : acc), 0)
            const isSel = selected.has(b.band)
            return (
              <button
                key={b.band}
                type="button"
                data-select-key={b.band}
                onClick={(e) => pick(b.band, e.shiftKey)}
                className="flex h-full flex-1 items-end"
                style={{ cursor: 'pointer', opacity: hasSel && !isSel ? 0.4 : 1 }}
                aria-pressed={isSel}
                aria-label={`Select age band ${b.band}`}
              >
                <div
                  className="flex w-full flex-col-reverse"
                  style={{
                    height: `${(total / max) * 100}%`,
                    boxShadow: isSel ? `0 0 0 2px ${SELECT_BLUE}` : 'none',
                  }}
                >
                  {b.seg.map((v, i) =>
                    v > 0 ? (
                      <div
                        key={i}
                        style={{
                          height: `${(v / total) * 100}%`,
                          backgroundColor: CONTRIB_SEGMENTS[i][1],
                          borderTopLeftRadius: i === topIndex ? 3 : 0,
                          borderTopRightRadius: i === topIndex ? 3 : 0,
                        }}
                      />
                    ) : null
                  )}
                </div>
              </button>
            )
          })}
        </div>
        <ShapeOverlay shape={shape} />
      </div>
      <div className="mt-2 flex gap-2 px-0.5">
        {AGE_CONTRIB.map((b) => (
          <span key={b.band} className="flex-1 text-center text-[10px] text-muted-foreground">
            {b.band}
          </span>
        ))}
      </div>
      <Legend items={CONTRIB_SEGMENTS} />
    </div>
  )
}

// Mirrors the app's heatmap color buckets exactly (Visualizations.jsx getColor).
function heatColor(value) {
  if (value === null) return 'hsl(215 20% 96%)'
  if (value === 0) return 'hsl(0 78% 94%)'
  if (value < 4) return 'hsl(32 88% 92%)'
  if (value < 8) return 'hsl(45 90% 90%)'
  if (value < 12) return 'hsl(173 56% 88%)'
  return 'hsl(217 72% 88%)'
}

const HEATMAP = [
  { band: 'Under 25', m: [0, 0], f: [0, 0] },
  { band: '25–34', m: [5, 10], f: [5, 18] },
  { band: '35–44', m: [7.5, 12], f: [7.7, 16] },
  { band: '45–54', m: [9.2, 12], f: [10.6, 7] },
  { band: '55–64', m: [11.8, 10], f: [9.9, 5] },
  { band: '65+', m: [12.9, 3], f: [15.5, 2] },
]

/** Age × gender deferral heatmap, styled after the in-app table. Click or drag to select. */
function DeferralHeatmap({ selected, onSelect, onPreview }) {
  const hasSel = selected.size > 0
  const pick = (key, additive) => {
    if (!additive) {
      onSelect(new Set([key]))
      return
    }
    const next = new Set(selected)
    if (next.has(key)) next.delete(key)
    else next.add(key)
    onSelect(next)
  }
  const { plotRef, tool, setTool, shape } = useChartShapeSelect({ selected, onPreview, onCommit: onSelect, getKeys: domKeysForShape })

  const cell = (band, gender, def) => {
    const key = `${band}|${gender}`
    const isSel = selected.has(key)
    return (
      <td className="p-1 text-center">
        <button
          type="button"
          data-select-key={key}
          onClick={(e) => pick(key, e.shiftKey)}
          className="w-full px-3 py-1.5 text-[oklch(0.2_0.01_55)] transition-opacity"
          aria-pressed={isSel}
          style={{
            backgroundColor: heatColor(def),
            cursor: 'pointer',
            opacity: hasSel && !isSel ? 0.45 : 1,
            boxShadow: isSel ? `inset 0 0 0 2px ${SELECT_BLUE}` : 'none',
          }}
        >
          {def.toFixed(1)}%
        </button>
      </td>
    )
  }

  return (
    <div>
      <ShapeToggle tool={tool} setTool={setTool} />
      <div ref={plotRef} className="relative select-none" style={{ touchAction: 'pan-y' }}>
        <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="px-3 py-2 text-left font-medium text-muted-foreground">Age band</th>
            <th className="px-3 py-2 text-center font-medium text-muted-foreground">Male</th>
            <th className="px-3 py-2 text-center font-medium text-muted-foreground">Female</th>
          </tr>
        </thead>
        <tbody>
          {HEATMAP.map((row) => (
            <tr key={row.band}>
              <td className="px-3 py-1.5 font-medium text-muted-foreground">{row.band}</td>
              {cell(row.band, 'M', row.m[0])}
              {cell(row.band, 'F', row.f[0])}
            </tr>
          ))}
        </tbody>
        </table>
        <ShapeOverlay shape={shape} />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span>Avg deferral:</span>
        <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(0 78% 94%)' }}>0%</span>
        <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(32 88% 92%)' }}>1–4%</span>
        <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(45 90% 90%)' }}>4–8%</span>
        <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(173 56% 88%)' }}>8–12%</span>
        <span className="px-2 py-0.5 text-[oklch(0.488_0.01_55)]" style={{ backgroundColor: 'hsl(217 72% 88%)' }}>12%+</span>
      </div>
    </div>
  )
}
